import { useState, useMemo, useEffect, useCallback } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { products } from "../data/products";
import { Link } from "react-router-dom";
import lightBulbIcon from "../assets/images/lightbulb.png";
import LoadingScreen from "../components/common/LoadingScreen";
import ErrorScreen from "../components/common/ErrorScreen";
import { useDelayedLoading } from "../hooks/useDelayedLoading";
import SEO from "../components/SEO";

const Products = () => {
  const availableLightNumbers = useMemo(() => {
    return [...new Set(products.map((p) => p.lightNum))].sort((a, b) => a - b);
  }, []);
  const [lightRange, setLightRange] = useState([
    availableLightNumbers[0],
    availableLightNumbers[availableLightNumbers.length - 1],
  ]);
  const currentMinIndex = availableLightNumbers.indexOf(lightRange[0]);
  const currentMaxIndex = availableLightNumbers.indexOf(lightRange[1]);

  const filteredProducts = useMemo(() => {
    return products.filter(
      (product) =>
        product.lightNum >= lightRange[0] && product.lightNum <= lightRange[1]
    );
  }, [lightRange]);

  const handleRangeChange = (isMin, index) => {
    const newIndex = parseInt(index);
    if (isMin) {
      const maxAllowedIndex = currentMaxIndex;
      const boundedIndex = Math.min(newIndex, maxAllowedIndex);
      setLightRange([availableLightNumbers[boundedIndex], lightRange[1]]);
    } else {
      const minAllowedIndex = currentMinIndex;
      const boundedIndex = Math.max(newIndex, minAllowedIndex);
      setLightRange([lightRange[0], availableLightNumbers[boundedIndex]]);
    }
  };

  const updateRangeBackground = useCallback(() => {
    const rangeSlider = document.querySelector(".range-slider");
    if (rangeSlider) {
      const leftPercent =
        (currentMinIndex / (availableLightNumbers.length - 1)) * 100;
      const rightPercent =
        100 - (currentMaxIndex / (availableLightNumbers.length - 1)) * 100;
      rangeSlider.style.setProperty("--left-value", `${leftPercent}%`);
      rangeSlider.style.setProperty("--right-value", `${rightPercent}%`);
    }
  }, [currentMinIndex, currentMaxIndex, availableLightNumbers.length]);

  useEffect(() => {
    updateRangeBackground();
  }, [lightRange, updateRangeBackground]);

  // Create array of loading promises for product images
  const loadingPromises = useMemo(() => {
    return products.map((product) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = product.image;
        img.onload = resolve;
        img.onerror = resolve;
      });
    });
  }, []);

  // Use the custom hook
  const { isLoading, error } = useDelayedLoading(loadingPromises);

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <SEO
        title="Premium Crystal Chandeliers Collection"
        description="Browse our extensive collection of luxury crystal chandeliers. Classic, modern, French, and contemporary designs available. Custom dimensions and premium crystals from Swarovski and Asfour. Professional installation included."
        keywords="crystal chandeliers for sale, luxury chandeliers, French chandeliers, modern chandeliers, classic chandeliers, Swarovski chandeliers, Asfour crystals, chandelier collection Johannesburg"
        url="/products"
        image="/assets/images/products/classic-24.jpeg"
      />
      <ProductsContainer>
      <LightRangeFilter>
        <span>
          Chandelier Size - Number of Lights: {lightRange[0]} - {lightRange[1]}
        </span>
        <RangeSlider className="range-slider">
          <input
            type="range"
            min={0}
            max={availableLightNumbers.length - 1}
            value={currentMinIndex}
            onChange={(e) => handleRangeChange(true, e.target.value)}
            step={1}
          />
          <input
            type="range"
            min={0}
            max={availableLightNumbers.length - 1}
            value={currentMaxIndex}
            onChange={(e) => handleRangeChange(false, e.target.value)}
            step={1}
          />
        </RangeSlider>
      </LightRangeFilter>

      <ProductGrid>
        <AnimatePresence>
          {filteredProducts.map((product) => (
            <motion.div
              key={product.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.5 }}
            >
              <Link
                to={`/product/${product.id}`}
                style={{ textDecoration: "none" }}
              >
                <ProductCard>
                  <ImageContainer>
                    <img src={product.image} alt={product.name} />
                    {product.isNew && <NewBadge>New</NewBadge>}
                  </ImageContainer>
                  <ProductInfo>
                    <ProductName>{product.name}</ProductName>
                    <ProductDetails>
                      <span>Dimensions: {product.dimensions}</span>
                    </ProductDetails>
                    <Button primary>View Details</Button>
                  </ProductInfo>
                </ProductCard>
              </Link>
            </motion.div>
          ))}
        </AnimatePresence>
      </ProductGrid>
    </ProductsContainer>
    </>
  );
};

const ProductsContainer = styled.div`
  max-width: 1800px;
  margin: 8rem auto 4rem;
  padding: 0 2rem;

  h1 {
    text-align: center;
    font-size: 3.5rem;
    color: ${(props) => props.theme.colors.accent};
    margin-bottom: 3rem;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(450px, 1fr));
  gap: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled.div`
  background: rgba(44, 44, 44, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(192, 160, 128, 0.2);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3),
      0 0 30px rgba(192, 160, 128, 0.1);
    border-color: ${(props) => props.theme.colors.accent};
  }
`;

const ImageContainer = styled.div`
  position: relative;
  height: 500px;
  width: 100%;
  overflow: hidden;
  background: ${(props) => props.theme.colors.primary};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 1rem;
    transition: transform 0.5s ease;
    background: linear-gradient(
      to bottom,
      rgba(44, 44, 44, 0.8),
      rgba(26, 26, 26, 0.9)
    );
  }

  ${ProductCard}:hover img {
    transform: scale(1.03);
  }
`;

const NewBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.primary};
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 600;
`;

const ProductInfo = styled.div`
  padding: 2rem;
`;

const ProductName = styled.h2`
  font-size: 1.8rem;
  margin: 0.5rem 0;
  color: ${(props) => props.theme.colors.text};
`;

const ProductDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem 0;

  span {
    color: ${(props) => props.theme.colors.textSecondary};
    font-size: 0.9rem;
  }
`;

const Button = styled.button`
  padding: 0.8rem 1.5rem;
  border: 2px solid ${(props) => props.theme.colors.accent};
  background: ${(props) =>
    props.primary ? props.theme.colors.accent : "transparent"};
  color: ${(props) =>
    props.primary ? props.theme.colors.primary : props.theme.colors.accent};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(192, 160, 128, 0.2);
  }
`;

const LightRangeFilter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 3rem auto;
  width: 100%;
  max-width: 800px;
  padding: 0 2rem;

  span {
    margin-bottom: 1rem;
    color: ${(props) => props.theme.colors.text};
    font-size: 1.1rem;
  }
`;

const RangeSlider = styled.div`
  position: relative;
  width: 100%;
  height: 50px;
  margin: 0 auto;

  input[type="range"] {
    position: absolute;
    width: 100%;
    height: 5px;
    background: none;
    pointer-events: none;
    -webkit-appearance: none;
    z-index: 3;

    &::-webkit-slider-thumb {
      height: 30px;
      width: 30px;
      border-radius: 50%;
      background: ${(props) => props.theme.colors.accent};
      cursor: pointer;
      pointer-events: auto;
      -webkit-appearance: none;
      background-image: url(${lightBulbIcon});
      background-size: cover;
      background-position: center;
      box-shadow: 0 0 10px rgba(192, 160, 128, 0.5);
      transition: transform 0.2s ease;

      &:hover {
        transform: scale(1.1);
      }
    }

    &::-moz-range-thumb {
      height: 30px;
      width: 30px;
      border-radius: 50%;
      background: ${(props) => props.theme.colors.accent};
      cursor: pointer;
      pointer-events: auto;
      background-image: url(${lightBulbIcon});
      background-size: cover;
      background-position: center;
      box-shadow: 0 0 10px rgba(192, 160, 128, 0.5);
      transition: transform 0.2s ease;

      &:hover {
        transform: scale(1.1);
      }
    }

    &::-webkit-slider-runnable-track {
      background: none;
      height: 5px;
    }

    &::-moz-range-track {
      background: none;
      height: 5px;
    }

    &:focus {
      outline: none;
    }
  }

  &::before {
    content: "";
    position: absolute;
    width: 100%;
    height: 5px;
    background: rgba(192, 160, 128, 0.2);
    top: 50%;
    transform: translateY(-50%);
    border-radius: 5px;
    z-index: 1;
  }

  &::after {
    content: "";
    position: absolute;
    height: 5px;
    background: ${(props) => props.theme.colors.accent};
    top: 50%;
    transform: translateY(-50%);
    border-radius: 5px;
    z-index: 2;
    left: var(--left-value, 0%);
    right: var(--right-value, 0%);
  }
`;

export default Products;
